/* ==========================================================================
   Monica & Me — mobile behaviour layer  (方案一 + 方案二)
   --------------------------------------------------------------------------
   Everything here is progressive enhancement. If this file fails to load the
   store still works: the tab bar renders (server-side), the mobile stylesheet
   still applies, and the desktop experience is untouched.

   Contents
     01  small helpers
     02  bottom tab bar
     03  PDP sticky add-to-bag bar
     04  PDP swipeable gallery dots
     05  mobile-template link rewriting
     06  view switch (mobile <-> desktop)
   ========================================================================== */

(function () {
  'use strict';

  /* ---------- 01 small helpers ---------- */

  var MOBILE_MQ = window.matchMedia('(max-width: 749px)');

  function isSmall() {
    return MOBILE_MQ.matches;
  }

  function onSmallChange(fn) {
    if (MOBILE_MQ.addEventListener) MOBILE_MQ.addEventListener('change', fn);
    else if (MOBILE_MQ.addListener) MOBILE_MQ.addListener(fn);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  // Coalesce a burst of calls into one update. requestAnimationFrame is the
  // right tool, but it is throttled in background tabs, off-screen iframes and
  // some in-app browsers — and a throttled rAF would leave the coalescing flag
  // stuck, so nothing would ever repaint. The timeout is the safety net.
  function coalesce(state, fn) {
    if (state.pending) return;
    state.pending = true;
    var done = false;
    function run() {
      if (done) return;
      done = true;
      state.pending = false;
      fn();
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
    setTimeout(run, 60);
  }

  var IS_MOBILE_TEMPLATE = document.documentElement.getAttribute('data-mm-view') === 'mobile';

  /* ---------- 02 bottom tab bar ---------- */

  // The tab bar itself is rendered server-side (snippets/mm-mobile-tabbar.liquid)
  // with the current page already marked, and its bag badge reuses the shared
  // .mm-cart-count class so the updater in mm-theme.js keeps it in sync.
  // Nothing left to wire here — the search and bag tabs ride on the existing
  // [data-open-search] / [data-open-cart] handlers.

  /* ---------- 03 PDP sticky add-to-bag bar ---------- */

  function initStickyAtc() {
    var bar = document.querySelector('[data-mm-atc-bar]');
    if (!bar) return;

    var root = document.querySelector('[data-mm-pdp]');
    var form = root && root.querySelector('.mm-pdp-form');
    var mainSubmit = root && root.querySelector('.mm-pdp-submit');
    var select = root && root.querySelector('#ProductVariant');
    if (!root || !form || !mainSubmit) return;

    var priceOut = bar.querySelector('[data-mm-atc-price]');
    var hintOut = bar.querySelector('[data-mm-atc-hint]');
    var button = bar.querySelector('[data-mm-atc-submit]');
    var priceSource = root.querySelector('[data-price-current]');

    // Take the bar out of [hidden] so it can slide instead of popping, then
    // drive visibility purely with the .is-visible class.
    bar.hidden = false;

    function sync() {
      if (priceSource && priceOut) priceOut.textContent = priceSource.textContent.trim();

      var hint = '';
      if (select && select.options.length) {
        var option = select.options[select.selectedIndex];
        if (option) hint = option.text.replace(/\s*-\s*Sold out\s*$/i, '').trim();
      }
      if (hintOut) hintOut.textContent = hint;

      if (button) {
        button.disabled = !!mainSubmit.disabled;
        var label = (mainSubmit.textContent || '').trim();
        button.textContent = label || 'Add to bag';
      }
    }

    // The PDP script rewrites the submit button's label/disabled state and the
    // price node in place, so mirror those nodes instead of re-deriving the
    // variant ourselves — one source of truth, no drift.
    var syncState = { pending: false };

    var visible = false;
    function setVisible(next) {
      if (next === visible) return;
      visible = next;
      bar.classList.toggle('is-visible', next);
      document.body.classList.toggle('mm-atc-active', next);
    }

    // Show the bar once the inline button has scrolled off the top of the
    // screen — never while the shopper is still reading the gallery.
    //
    // This is a scroll listener rather than an IntersectionObserver on purpose:
    // it reads the live rect, so it cannot be wrong because a callback arrived
    // late or carried a stale position, and it re-checks when the PDP reflows
    // (opening an accordion, switching variant) instead of only when the button
    // crosses a threshold. One coalesced getBoundingClientRect per frame.
    function evaluate() {
      var rect = mainSubmit.getBoundingClientRect();
      setVisible(rect.bottom < 0 && isSmall());
    }

    var syncSoonState = { pending: false };
    function syncSoon() {
      coalesce(syncSoonState, function () {
        sync();
        evaluate();
      });
    }

    var scrollState = { pending: false };
    function evaluateSoon() {
      coalesce(scrollState, evaluate);
    }

    window.addEventListener('scroll', evaluateSoon, { passive: true });
    window.addEventListener('resize', evaluateSoon);
    window.addEventListener('orientationchange', evaluateSoon);

    new MutationObserver(syncSoon).observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled']
    });
    if (select) select.addEventListener('change', syncSoon);

    button.addEventListener('click', function () {
      if (button.disabled) return;
      // Reuse the real form so quantity, variant id and the AJAX handler all
      // behave exactly as they do on the inline button.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(mainSubmit);
      } else {
        mainSubmit.click();
      }
    });

    function applyViewport() {
      if (!isSmall()) setVisible(false);
      else evaluate();
    }
    onSmallChange(applyViewport);

    sync();
    evaluate();
  }

  /* ---------- 04 PDP swipeable gallery dots ---------- */

  function initGalleryDots() {
    var gallery = document.querySelector('.mm-pdp-gallery');
    if (!gallery) return;

    var dots = null;
    var tiles = [];

    function build() {
      teardown();
      tiles = Array.prototype.slice.call(gallery.querySelectorAll('.mm-pdp-tile'));
      if (tiles.length < 2 || !isSmall()) return;

      dots = document.createElement('div');
      dots.className = 'mm-pdp-dots';
      dots.setAttribute('role', 'tablist');
      dots.setAttribute('aria-label', 'Product images');

      tiles.forEach(function (tile, index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Image ' + (index + 1));
        dot.addEventListener('click', function () {
          gallery.scrollTo({ left: tile.offsetLeft, behavior: 'smooth' });
        });
        dots.appendChild(dot);
      });

      gallery.parentNode.insertBefore(dots, gallery.nextSibling);
      gallery.addEventListener('scroll', onScroll, { passive: true });
      // Mark the first dot immediately — don't make the initial state wait on a
      // frame, which a background tab or an off-screen container can defer.
      if (dots.firstElementChild) {
        dots.firstElementChild.classList.add('is-active');
        dots.firstElementChild.setAttribute('aria-selected', 'true');
      }
      onScroll();
    }

    function teardown() {
      gallery.removeEventListener('scroll', onScroll);
      if (dots && dots.parentNode) dots.parentNode.removeChild(dots);
      dots = null;
      tiles = [];
    }

    var scrollState = { pending: false };
    function onScroll() {
      if (!dots) return;
      coalesce(scrollState, paintDots);
    }

    function paintDots() {
      if (!dots || !tiles.length) return;
      var width = gallery.clientWidth || 1;
      var index = Math.round(gallery.scrollLeft / width);
      if (index < 0) index = 0;
      if (index > tiles.length - 1) index = tiles.length - 1;
      Array.prototype.forEach.call(dots.children, function (dot, i) {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    build();
    onSmallChange(build);
  }

  /* ---------- 05 mobile-template link rewriting ---------- */

  // On ?view=mobile every internal link keeps the mobile template, so a tap
  // never bounces the shopper back to the responsive layout. Skipped for
  // checkout/account/apps, where Shopify hosts the page and ?view is noise.
  var SKIP_PREFIXES = ['/checkout', '/account', '/apps', '/password', '/cart/'];

  function shouldSkip(path) {
    for (var i = 0; i < SKIP_PREFIXES.length; i++) {
      if (path.indexOf(SKIP_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  function initLinkRewrite() {
    if (!IS_MOBILE_TEMPLATE) return;

    function rewrite(scope) {
      var links = (scope || document).querySelectorAll('a[href]');
      Array.prototype.forEach.call(links, function (a) {
        var href = a.getAttribute('href');
        if (!href) return;
        if (href.charAt(0) !== '/' && href.indexOf(location.origin) !== 0) return;
        if (href.charAt(0) === '#') return;
        if (href.indexOf('view=mobile') !== -1) return;

        var url;
        try {
          url = new URL(a.href, location.origin);
        } catch (e) {
          return;
        }
        if (url.origin !== location.origin) return;
        if (shouldSkip(url.pathname)) return;

        url.searchParams.set('view', 'mobile');
        a.setAttribute('href', url.pathname + url.search + url.hash);
      });
    }

    rewrite(document);
    document.addEventListener('shopify:section:load', function (event) {
      rewrite(event.target);
    });
  }

  /* ---------- 06 view switch ---------- */

  var COOKIE = 'mm_view';
  var COOKIE_DAYS = 30;

  function setCookie(value) {
    var expires = new Date(Date.now() + COOKIE_DAYS * 864e5).toUTCString();
    document.cookie = COOKIE + '=' + value + '; expires=' + expires + '; path=/; SameSite=Lax';
  }

  function initViewSwitch() {
    var links = document.querySelectorAll('[data-mm-view-switch]');
    Array.prototype.forEach.call(links, function (link) {
      link.addEventListener('click', function () {
        var target = link.getAttribute('data-mm-view-target');
        setCookie(target === 'mobile' ? 'mobile' : 'desktop');
      });
    });
  }

  /* ---------- boot ---------- */

  onReady(function () {
    initStickyAtc();
    initGalleryDots();
    initLinkRewrite();
    initViewSwitch();
  });
})();
